import {
  integrationConfig,
  isBackendConfigured,
} from './integrationConfig'

import type {
  MembershipPlanId,
  PractitionerAccountInput,
  PractitionerApiErrorCode,
  PractitionerSession,
} from '../types/practitionerSignup'

/**
 * Browser → Supabase integration for the web practitioner signup flow.
 *
 * Architecture:
 *
 *   Website
 *      └──► Edge Function `create-practitioner-account`
 *              └──► Supabase Auth
 *
 *   Website
 *      └──► Supabase Auth password grant
 *              └──► practitioner session JWT
 *
 *   Website
 *      └──► Edge Function `create-practitioner-subscription`
 *              └──► Stripe Checkout Session
 *                      └──► Stripe-hosted payment page
 *                              └──► Stripe subscription + 2-month trial
 *
 *   Stripe
 *      └──► Edge Function `stripe-webhook`
 *              └──► practitioner_subscriptions
 *
 *   Website
 *      └──► Edge Function `create-practitioner-connect-account`
 *              └──► Stripe Connect Express account
 *                      └──► Stripe-hosted onboarding
 *                              └──► practitioner payout setup
 *
 *   Website
 *      └──► Edge Function `get-practitioner-connect-status`
 *              └──► Stripe Connect account
 *                      └──► synchronize payment/payout status
 *
 * SECURITY
 * - Passwords are sent over HTTPS and are never logged.
 * - Never put Supabase service_role/secret keys or Stripe secret keys
 *   in frontend code.
 * - Protected Edge Functions verify the authenticated practitioner JWT.
 * - Stripe card details are collected directly by Stripe Checkout.
 * - Stripe Connect identity/bank information is collected directly by Stripe.
 */

export class PractitionerApiError extends Error {
  code: PractitionerApiErrorCode

  constructor(
    code: PractitionerApiErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'PractitionerApiError'
    this.code = code
  }
}

function assertConfigured() {
  if (!isBackendConfigured()) {
    throw new PractitionerApiError(
      'not_configured',
      'Practitioner signup is not connected yet.',
    )
  }
}

function publicHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    apikey: integrationConfig.supabaseAnonKey,
  }
}

async function readJson(
  response: Response,
): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<
      string,
      unknown
    >
  } catch {
    return {}
  }
}

async function request(
  url: string,
  init: RequestInit,
): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch {
    throw new PractitionerApiError(
      'network',
      'We couldn’t reach Fem Entity. Check your connection and try again.',
    )
  }
}

function serverMessage(
  body: Record<string, unknown>,
): string | undefined {
  const candidates = [
    body.error,
    body.message,
    body.msg,
    body.error_description,
  ]

  for (const candidate of candidates) {
    if (
      typeof candidate === 'string' &&
      candidate.trim()
    ) {
      return candidate
    }

    if (
      candidate &&
      typeof candidate === 'object'
    ) {
      const nested = (
        candidate as Record<string, unknown>
      ).message

      if (
        typeof nested === 'string' &&
        nested.trim()
      ) {
        return nested
      }
    }
  }

  return undefined
}

/**
 * True when the Supabase response indicates
 * that the email already exists.
 */
function isDuplicateEmail(
  body: Record<string, unknown>,
): boolean {
  const code =
    typeof body.code === 'string'
      ? body.code
      : typeof body.error_code === 'string'
        ? body.error_code
        : ''

  if (
    code === 'email_exists' ||
    code === 'user_already_exists'
  ) {
    return true
  }

  const text = (
    serverMessage(body) ?? ''
  ).toLowerCase()

  return (
    text.includes('already') &&
    (
      text.includes('registered') ||
      text.includes('exist')
    )
  )
}

/**
 * Headers for calling an Edge Function
 * without a user session.
 *
 * New-format publishable keys (`sb_publishable_...`)
 * are not JWTs, so they are sent in the `apikey`
 * header only.
 *
 * Legacy anon JWT keys are also sent in the
 * Bearer token.
 */
function anonymousFunctionHeaders(): Record<
  string,
  string
> {
  const headers = publicHeaders()

  if (
    !integrationConfig.supabaseAnonKey.startsWith(
      'sb_',
    )
  ) {
    headers.Authorization =
      `Bearer ${integrationConfig.supabaseAnonKey}`
  }

  return headers
}

/**
 * Temporary practitioner website session storage.
 *
 * Stripe Checkout and Stripe Connect both leave
 * the FemEntity website and redirect back later.
 *
 * React memory does not survive that navigation.
 * We use localStorage so the authenticated
 * practitioner session survives the external
 * Stripe redirect.
 *
 * The stored access token still expires normally.
 * Expired or malformed sessions are removed
 * before they can be used.
 */
const PRACTITIONER_SESSION_KEY =
  'fementity_practitioner_session'

export function savePractitionerSession(
  session: PractitionerSession,
): void {
  try {
    localStorage.setItem(
      PRACTITIONER_SESSION_KEY,
      JSON.stringify(session),
    )
  } catch {
    // If browser storage is unavailable,
    // the practitioner can sign in again.
  }
}

export function getStoredPractitionerSession():
  | PractitionerSession
  | null {
  try {
    const raw = localStorage.getItem(
      PRACTITIONER_SESSION_KEY,
    )

    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as
      Partial<PractitionerSession>

    if (
      typeof parsed.accessToken !== 'string' ||
      !parsed.accessToken ||
      typeof parsed.userId !== 'string' ||
      !parsed.userId ||
      typeof parsed.email !== 'string' ||
      !parsed.email ||
      typeof parsed.expiresAt !== 'number'
    ) {
      localStorage.removeItem(
        PRACTITIONER_SESSION_KEY,
      )

      return null
    }

    /**
     * Consider the session expired slightly early
     * so we do not start an Edge Function request
     * with a token that is about to expire.
     */
    if (
      parsed.expiresAt - 60_000 <= Date.now()
    ) {
      localStorage.removeItem(
        PRACTITIONER_SESSION_KEY,
      )

      return null
    }

    return {
      accessToken: parsed.accessToken,
      userId: parsed.userId,
      email: parsed.email,
      expiresAt: parsed.expiresAt,
    }
  } catch {
    return null
  }
}

export function clearStoredPractitionerSession(): void {
  try {
    localStorage.removeItem(
      PRACTITIONER_SESSION_KEY,
    )
  } catch {
    // Nothing else required.
  }
}

/**
 * Create the practitioner account through
 * the Supabase Edge Function.
 */
export async function createPractitionerAccount(
  input: PractitionerAccountInput,
): Promise<void> {
  console.log('SUPABASE CONFIG CHECK', {
    supabaseUrl:
      integrationConfig.supabaseUrl,

    hasAnonKey:
      Boolean(
        integrationConfig.supabaseAnonKey,
      ),

    createAccountFunction:
      integrationConfig.createAccountFunction,

    backendConfigured:
      isBackendConfigured(),
  })

  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.createAccountFunction}`,
    {
      method: 'POST',
      headers: anonymousFunctionHeaders(),
      body: JSON.stringify(input),
    },
  )

  const body = await readJson(response)

  const reportedError =
    body.success === false ||
    (
      body.error !== undefined &&
      body.error !== null
    )

  if (
    response.ok &&
    !reportedError
  ) {
    return
  }

  if (
    response.status === 409 ||
    isDuplicateEmail(body)
  ) {
    throw new PractitionerApiError(
      'email_exists',
      'An account with this email already exists.',
    )
  }

  throw new PractitionerApiError(
    'server',
    serverMessage(body) ??
      'We couldn’t create your account. Please try again.',
  )
}

/**
 * Sign in the practitioner through Supabase Auth.
 */
export async function signInPractitioner(
  email: string,
  password: string,
): Promise<PractitionerSession> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/auth/v1/token?grant_type=password`,
    {
      method: 'POST',

      headers: publicHeaders(),

      body: JSON.stringify({
        email,
        password,
      }),
    },
  )

  const body = await readJson(response)

  if (!response.ok) {
    const code =
      typeof body.error_code === 'string'
        ? body.error_code
        : ''

    if (
      code === 'email_not_confirmed'
    ) {
      throw new PractitionerApiError(
        'email_not_confirmed',
        'Please confirm your email address, then sign in to continue.',
      )
    }

    if (
      response.status === 400 ||
      code === 'invalid_credentials'
    ) {
      throw new PractitionerApiError(
        'invalid_credentials',
        'That email and password don’t match our records.',
      )
    }

    throw new PractitionerApiError(
      'server',
      serverMessage(body) ??
        'We couldn’t sign you in. Please try again.',
    )
  }

  const user = (
    body.user ?? {}
  ) as {
    id?: string
    email?: string
  }

  const accessToken =
    typeof body.access_token === 'string'
      ? body.access_token
      : ''

  const expiresIn =
    typeof body.expires_in === 'number'
      ? body.expires_in
      : 3600

  if (
    !accessToken ||
    !user.id
  ) {
    throw new PractitionerApiError(
      'server',
      'We couldn’t start your session. Please try again.',
    )
  }

  const session: PractitionerSession = {
    accessToken,

    userId:
      user.id,

    email:
      user.email ?? email,

    expiresAt:
      Date.now() +
      expiresIn * 1000,
  }

  /**
   * Preserve the session across Stripe redirects.
   */
  savePractitionerSession(session)

  return session
}

/**
 * Response returned by the
 * `create-practitioner-subscription`
 * Supabase Edge Function.
 *
 * The Edge Function creates a Stripe Checkout
 * Session.
 */
export interface PractitionerSubscriptionResponse {
  success: boolean
  existingSubscription?: boolean
  checkoutUrl: string
  checkoutSessionId: string
  customerId: string
  tierName: MembershipPlanId
  planType: 'monthly' | 'annual'
  trialEndDate: string
}

/**
 * Start Stripe Checkout for the practitioner's
 * membership.
 *
 * Membership billing is separate from Stripe Connect.
 */
export async function startPractitionerSubscription(
  planId: MembershipPlanId,
  session: PractitionerSession,
): Promise<PractitionerSubscriptionResponse> {
  assertConfigured()

  /**
   * Make sure the session is available when Stripe
   * sends the practitioner back to FemEntity.
   */
  savePractitionerSession(session)

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.checkoutFunction}`,
    {
      method: 'POST',

      headers: {
        ...publicHeaders(),

        Authorization:
          `Bearer ${session.accessToken}`,
      },

      body: JSON.stringify({
        userId:
          session.userId,

        tierName:
          planId,

        planType:
          'monthly',

        /**
         * Backend currently uses SITE_URL for
         * Stripe success/cancel URLs.
         */
        returnOrigin:
          window.location.origin,
      }),
    },
  )

  const body = await readJson(response)

  if (!response.ok) {
    throw new PractitionerApiError(
      'server',
      serverMessage(body) ??
        'We couldn’t start your membership. Please try again.',
    )
  }

  /**
   * Validate the Checkout response.
   *
   * Payment is handled entirely by Stripe Checkout.
   */
  if (
    body.success !== true ||
    typeof body.checkoutUrl !== 'string' ||
    !body.checkoutUrl ||
    typeof body.checkoutSessionId !== 'string' ||
    !body.checkoutSessionId ||
    typeof body.customerId !== 'string' ||
    !body.customerId ||
    typeof body.tierName !== 'string' ||
    typeof body.planType !== 'string' ||
    typeof body.trialEndDate !== 'string'
  ) {
    throw new PractitionerApiError(
      'server',
      'We received an incomplete checkout response. Please try again.',
    )
  }

  const validPlanIds: MembershipPlanId[] = [
    'anchor',
    'bloom',
    'thrive',
    'collective',
  ]

  if (
    !validPlanIds.includes(
      body.tierName as MembershipPlanId,
    )
  ) {
    throw new PractitionerApiError(
      'server',
      'The selected membership plan is not valid. Please choose another plan.',
    )
  }

  if (
    body.planType !== 'monthly' &&
    body.planType !== 'annual'
  ) {
    throw new PractitionerApiError(
      'server',
      'The membership billing period is not valid. Please try again.',
    )
  }

  return {
    success: true,

    existingSubscription:
      body.existingSubscription === true,

    checkoutUrl:
      body.checkoutUrl,

    checkoutSessionId:
      body.checkoutSessionId,

    customerId:
      body.customerId,

    tierName:
      body.tierName as MembershipPlanId,

    planType:
      body.planType as
        | 'monthly'
        | 'annual',

    trialEndDate:
      body.trialEndDate,
  }
}

/**
 * Result of the membership confirmation check.
 */
export interface MembershipConfirmation {
  status:
    | 'confirmed'
    | 'pending'
    | 'failed'

  tierName:
    MembershipPlanId | null

  /**
   * ISO date the complimentary period ends,
   * if the backend provides it.
   */
  trialEndDate:
    string | null
}

const CHECKOUT_SESSION_PATTERN =
  /^cs_(test|live)_[A-Za-z0-9]+$/

export function isCheckoutSessionId(
  value: string | null,
): value is string {
  return Boolean(
    value &&
      CHECKOUT_SESSION_PATTERN.test(value),
  )
}

/**
 * Confirm the membership after Stripe Checkout.
 *
 * A Stripe success redirect by itself is not proof
 * that the subscription was successfully recorded.
 */
export async function getMembershipConfirmation(
  sessionId: string,
): Promise<MembershipConfirmation> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.membershipStatusFunction}`,
    {
      method: 'POST',

      headers:
        anonymousFunctionHeaders(),

      body: JSON.stringify({
        session_id:
          sessionId,
      }),
    },
  )

  const body =
    await readJson(response)

  if (!response.ok) {
    throw new PractitionerApiError(
      'server',

      serverMessage(body) ??
        'We couldn’t confirm your membership right now.',
    )
  }

  const validPlanIds: MembershipPlanId[] = [
    'anchor',
    'bloom',
    'thrive',
    'collective',
  ]

  const tierName =
    validPlanIds.includes(
      body.tierName as MembershipPlanId,
    )
      ? (
          body.tierName as MembershipPlanId
        )
      : null

  return {
    status:
      body.status === 'confirmed' ||
      body.status === 'failed'
        ? body.status
        : 'pending',

    tierName,

    trialEndDate:
      typeof body.trialEndDate === 'string'
        ? body.trialEndDate
        : null,
  }
}

/**
 * Response returned by
 * `create-practitioner-connect-account`.
 */
export interface PractitionerConnectResponse {
  success: boolean
  accountId: string
  onboardingUrl: string
}

/**
 * Create/reuse the practitioner's Stripe Connect
 * account and obtain a Stripe-hosted onboarding link.
 *
 * No userId is sent in the request body.
 *
 * The Edge Function identifies the practitioner
 * from the authenticated Supabase JWT.
 */
export async function startPractitionerConnectOnboarding(
  session: PractitionerSession,
): Promise<PractitionerConnectResponse> {
  assertConfigured()

  if (
    !session.accessToken ||
    session.expiresAt - 60_000 <= Date.now()
  ) {
    clearStoredPractitionerSession()

    throw new PractitionerApiError(
      'invalid_credentials',
      'Your session has expired. Please sign in again to set up payments.',
    )
  }

  /**
   * Preserve the session before leaving FemEntity.
   */
  savePractitionerSession(session)

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.connectAccountFunction}`,
    {
      method: 'POST',

      headers: {
        ...publicHeaders(),

        Authorization:
          `Bearer ${session.accessToken}`,
      },

      body:
        JSON.stringify({}),
    },
  )

  const body =
    await readJson(response)

  if (!response.ok) {
    if (
      response.status === 401
    ) {
      clearStoredPractitionerSession()
    }

    throw new PractitionerApiError(
      response.status === 401
        ? 'invalid_credentials'
        : 'server',

      serverMessage(body) ??
        'We couldn’t start your payment setup. Please try again.',
    )
  }

  if (
    body.success !== true ||
    typeof body.accountId !== 'string' ||
    !body.accountId ||
    typeof body.onboardingUrl !== 'string' ||
    !body.onboardingUrl
  ) {
    throw new PractitionerApiError(
      'server',
      'Stripe returned an incomplete payment setup response. Please try again.',
    )
  }

  /**
   * Validate that the backend really returned an
   * HTTPS Stripe URL before navigating the browser.
   */
  let onboardingUrl: URL

  try {
    onboardingUrl =
      new URL(
        body.onboardingUrl,
      )
  } catch {
    throw new PractitionerApiError(
      'server',
      'Stripe returned an invalid payment setup URL.',
    )
  }

  const hostname =
    onboardingUrl.hostname.toLowerCase()

  const isStripeHost =
    hostname === 'stripe.com' ||
    hostname.endsWith('.stripe.com')

  if (
    onboardingUrl.protocol !== 'https:' ||
    !isStripeHost
  ) {
    throw new PractitionerApiError(
      'server',
      'Stripe returned an unexpected payment setup URL.',
    )
  }

  return {
    success: true,

    accountId:
      body.accountId,

    onboardingUrl:
      onboardingUrl.toString(),
  }
}

/**
 * Current Stripe Connect state returned by
 * `get-practitioner-connect-status`.
 */
export type PractitionerConnectStatus =
  | 'not_started'
  | 'incomplete'
  | 'submitted'
  | 'ready'

export interface PractitionerConnectStatusResponse {
  success: boolean
  status: PractitionerConnectStatus
  detailsSubmitted: boolean
  chargesEnabled: boolean
  payoutsEnabled: boolean
}

/**
 * Retrieve the practitioner's current Stripe
 * Connect status.
 *
 * Stripe is the source of truth.
 *
 * The Edge Function retrieves the connected
 * account directly from Stripe and synchronizes
 * the current status back to practitioner_profiles.
 */
export async function getPractitionerConnectStatus(
  session: PractitionerSession,
): Promise<PractitionerConnectStatusResponse> {
  assertConfigured()

  if (
    !session.accessToken ||
    session.expiresAt - 60_000 <= Date.now()
  ) {
    clearStoredPractitionerSession()

    throw new PractitionerApiError(
      'invalid_credentials',
      'Your session has expired. Please sign in again to check your payment setup.',
    )
  }

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.connectStatusFunction}`,
    {
      method: 'POST',

      headers: {
        ...publicHeaders(),

        Authorization:
          `Bearer ${session.accessToken}`,
      },

      body:
        JSON.stringify({}),
    },
  )

  const body =
    await readJson(response)

  if (!response.ok) {
    if (
      response.status === 401
    ) {
      clearStoredPractitionerSession()
    }

    throw new PractitionerApiError(
      response.status === 401
        ? 'invalid_credentials'
        : 'server',

      serverMessage(body) ??
        'We couldn’t check your payment setup right now. Please try again.',
    )
  }

  const validStatuses:
    PractitionerConnectStatus[] = [
      'not_started',
      'incomplete',
      'submitted',
      'ready',
    ]

  if (
    body.success !== true ||
    typeof body.status !== 'string' ||
    !validStatuses.includes(
      body.status as PractitionerConnectStatus,
    ) ||
    typeof body.detailsSubmitted !== 'boolean' ||
    typeof body.chargesEnabled !== 'boolean' ||
    typeof body.payoutsEnabled !== 'boolean'
  ) {
    throw new PractitionerApiError(
      'server',
      'We received an incomplete payment status response. Please try again.',
    )
  }

  return {
    success: true,

    status:
      body.status as PractitionerConnectStatus,

    detailsSubmitted:
      body.detailsSubmitted,

    chargesEnabled:
      body.chargesEnabled,

    payoutsEnabled:
      body.payoutsEnabled,
  }
}

/**
 * Redirect the top-level browser to Stripe Connect.
 *
 * Magic Patterns previews may run inside an iframe.
 * Stripe onboarding must not be loaded inside that iframe.
 */
export function redirectToPractitionerConnect(
  onboardingUrl: string,
): void {
  if (window.top) {
    window.top.location.href =
      onboardingUrl

    return
  }

  window.location.href =
    onboardingUrl
}

/**
 * Convert API/runtime errors into a
 * user-friendly message.
 *
 * Uses structural checking instead of `instanceof`
 * because the Magic Patterns runtime can have
 * module-boundary issues with class-based
 * instanceof checks.
 */
export function describeError(
  error: unknown,
): string {
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (
      error as {
        message?: unknown
      }
    ).message === 'string'
  ) {
    return (
      error as {
        message: string
      }
    ).message
  }

  return 'Something went wrong. Please try again.'
}